import { Typography, colors, Badge } from '@sweep-io/sweep-design';
import { Box, Stack, Collapse } from '@mui/material';
import { useState, useEffect } from 'react';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableVariant,
} from '../../../common/table/TableTypes';
import { DotDivider } from '../../../common/DotDivider';
import { DataTableId } from '../../../common/table/utils';
import { VirtualScrollDataTable } from '../../../common/table/VirtualScrollDataTable';
import { DataTable } from '../../../common/table/DataTable';
import { getMetadataLabel, getNameWithPlural } from '../../tabs/getMetadataLabel';
import { ExpandButtonSimple } from '../../../common/ExpandButtonSimple';
import { useSearchContext } from '../../dependencies/search/SearchContext';
import { SearchResult } from '../../dependencies/search/SearchContext';

export enum TableHeaderVariant {
  withBackground = 'WITH_BACKGROUND',
  withoutBackground = 'WITHOUT_BACKGROUND',
  justResultCounter = 'RESULT_COUNTER',
  expandable = 'EXPANDABLE',
}

export interface TableWithHeaderProps {
  tableHeader: string;
  columns: DataTableBaseColumn[];
  rows: DataTableRow[];
  headerVariant?: TableHeaderVariant;
  onRowClick?: (
    e: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    row: DataTableRow,
    columnData: DataTableBaseColumn,
  ) => void;
  dataTableType?: string;
  section?: SearchResult['section'];
}

export const TableWithHeader = ({
  tableHeader,
  columns,
  rows,
  headerVariant = TableHeaderVariant.withoutBackground,
  onRowClick,
  dataTableType: dataTableType,
  section,
}: TableWithHeaderProps) => {
  const withBackground = headerVariant === TableHeaderVariant.withBackground;
  const isExpandable = headerVariant === TableHeaderVariant.expandable;
  const [isExpanded, setIsExpanded] = useState(false);

  const { searchTerm, hasTableResults } = useSearchContext();

  // Auto-expand table if it contains search results
  useEffect(() => {
    if (searchTerm && isExpandable && dataTableType && section) {
      const hasResults = hasTableResults(section, dataTableType);
      if (hasResults) {
        setIsExpanded(true);
      }
    }
  }, [searchTerm, isExpandable, dataTableType, section, hasTableResults]);

  // Reset expansion when search is cleared
  useEffect(() => {
    if (!searchTerm && isExpandable) {
      setIsExpanded(false);
    }
  }, [searchTerm, isExpandable]);

  return (
    <Stack sx={{ mr: '2px', height: '100%' }} pb={withBackground ? 2 : 0}>
      <Box>
        {isExpandable && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1,
              cursor: 'pointer',
            }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Typography variant="body" color={colors.grey[900]}>
              {getNameWithPlural(tableHeader)}
            </Typography>
            <Badge label={rows.length} variant="secondary" />
            <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
          </Box>
        )}

        {(withBackground || headerVariant === TableHeaderVariant.withoutBackground) &&
          !isExpandable && (
            <Box
              sx={{
                background: withBackground ? colors.grey[100] : 'transparent',
                p: withBackground ? '1px 8px' : 0,
                borderRadius: 0.5,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {!withBackground && <DotDivider dotColor={colors.grey[900]} />}

              <Typography
                variant={withBackground ? 'body-bold' : 'body'}
                color={withBackground ? colors.grey[800] : colors.grey[900]}
              >
                {getMetadataLabel(tableHeader) ?? tableHeader}
              </Typography>
            </Box>
          )}

        {TableHeaderVariant.justResultCounter === headerVariant && (
          <Box sx={{ pb: 3 }}>
            <Typography variant="caption">{rows.length} results</Typography>
          </Box>
        )}
      </Box>
      <Collapse in={!isExpandable || isExpanded}>
        {rows.length < 10 ? (
          <Stack pl={2} overflow="hidden" data-table-type={dataTableType}>
            <DataTable
              tableId={'NOT_RELEVANT' as DataTableId}
              columns={columns}
              rows={rows}
              onRowClick={onRowClick}
              variant={DataTableVariant.small}
            />
          </Stack>
        ) : (
          <Stack pl={2} height="500px" overflow="hidden" data-table-type={dataTableType}>
            <VirtualScrollDataTable
              tableId={'NOT_RELEVANT' as DataTableId}
              columns={columns}
              rows={rows}
              onRowClick={onRowClick}
              variant={DataTableVariant.small}
              exposeVirtuosoRef={true}
            />
          </Stack>
        )}
      </Collapse>
    </Stack>
  );
};
